import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ClipboardCheck, CheckCircle, XCircle, AlertTriangle, Camera,
  Tractor, Truck, Droplet, Zap, Shield, FileText, Gauge,
  Plus, History, BarChart3, RefreshCw, ChevronRight, Loader2,
  Send, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '@/lib/api';

// Icônes par catégorie
const CATEGORY_ICONS = {
  securite: Shield,
  mecanique: Tractor,
  electrique: Zap,
  hydraulique: Droplet,
  carrosserie: Truck,
  documents: FileText,
  niveaux: Gauge
};

const CATEGORY_COLORS = {
  securite: 'text-red-500',
  mecanique: 'text-blue-500',
  electrique: 'text-yellow-500',
  hydraulique: 'text-cyan-500',
  carrosserie: 'text-gray-500',
  documents: 'text-green-500',
  niveaux: 'text-orange-500'
};

// Composant pour un item de checklist
const ChecklistItem = ({ item, response, onResponseChange }) => {
  const Icon = CATEGORY_ICONS[item.categorie] || ClipboardCheck;
  const colorClass = CATEGORY_COLORS[item.categorie] || 'text-gray-500';
  
  const handleConformeChange = (conforme) => {
    onResponseChange({
      ...response,
      item_id: item.id,
      conforme
    });
  };
  
  return (
    <div className={`p-4 border rounded-lg ${response?.conforme === false ? 'border-red-300 bg-red-50 dark:bg-red-950/20' : response?.conforme ? 'border-green-300 bg-green-50 dark:bg-green-950/20' : ''}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 mt-0.5 ${colorClass}`} />
        
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium">{item.nom}</span>
              {item.obligatoire && (
                <Badge variant="destructive" className="text-xs">Obligatoire</Badge>
              )}
            </div>
            
            {/* Boutons OK / NOK */}
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={response?.conforme === true ? "default" : "outline"}
                className={response?.conforme === true ? "bg-green-500 hover:bg-green-600" : ""}
                onClick={() => handleConformeChange(true)}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                OK
              </Button>
              <Button
                size="sm"
                variant={response?.conforme === false ? "default" : "outline"}
                className={response?.conforme === false ? "bg-red-500 hover:bg-red-600" : ""}
                onClick={() => handleConformeChange(false)}
              >
                <XCircle className="w-4 h-4 mr-1" />
                NOK
              </Button>
            </div>
          </div>
          
          {item.description && (
            <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
          )}
          
          {/* Champ valeur si attendu */}
          {item.valeur_attendue && (
            <div className="mt-2 flex items-center gap-2">
              <Input
                type="text"
                placeholder={item.valeur_attendue}
                className="w-32"
                value={response?.valeur || ''}
                onChange={(e) => onResponseChange({
                  ...response,
                  item_id: item.id,
                  valeur: e.target.value
                })}
              />
              {item.unite && <span className="text-sm text-muted-foreground">{item.unite}</span>}
            </div>
          )}
          
          {/* Commentaire si non conforme ou requis */}
          {(response?.conforme === false || item.necessite_commentaire) && (
            <Textarea
              placeholder="Commentaire..."
              className="mt-2"
              rows={2}
              value={response?.commentaire || ''}
              onChange={(e) => onResponseChange({
                ...response,
                item_id: item.id,
                commentaire: e.target.value
              })}
            />
          )}
          
          {/* Photo si requise */}
          {item.necessite_photo && (
            <div className="mt-2">
              <Button variant="outline" size="sm">
                <Camera className="w-4 h-4 mr-1" />
                Ajouter photo
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Page principale
export default function ChecklistMaterielPage() {
  const [templates, setTemplates] = useState([]);
  const [historique, setHistorique] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('nouvelle');
  
  // État pour nouvelle checklist
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [materielId, setMaterielId] = useState('');
  const [responses, setResponses] = useState({});
  const [commentaireGeneral, setCommentaireGeneral] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resultat, setResultat] = useState(null);
  
  // Charger les données
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesRes, historiqueRes, statsRes] = await Promise.all([
        api.get('/checklist-materiel/templates'),
        api.get('/checklist-materiel/historique?limit=20'),
        api.get('/checklist-materiel/stats')
      ]);
      
      setTemplates(templatesRes.data.templates || []);
      setHistorique(historiqueRes.data.checklists || []);
      setStats(statsRes.data);
    } catch (e) {
      console.log('Erreur chargement:', e);
    } finally {
      setLoading(false);
    }
  };
  
  // Générer template par défaut
  const genererTemplate = async (typeMateriel) => {
    try {
      const res = await api.post(`/checklist-materiel/templates/generer-defaut/${typeMateriel}`);
      toast.success(`Template ${typeMateriel} créé avec ${res.data.items_count} items`);
      loadData();
    } catch (e) {
      toast.error('Erreur création template');
    }
  };
  
  // Gérer les réponses
  const handleResponseChange = (response) => {
    setResponses(prev => ({
      ...prev,
      [response.item_id]: response
    }));
  };
  
  // Soumettre la checklist
  const handleSubmit = async () => {
    if (!selectedTemplate || !materielId) {
      toast.error('Sélectionnez un template et un matériel');
      return;
    }
    
    // Vérifier que tous les items ont une réponse
    const items = selectedTemplate.items || [];
    const itemsNonRepondus = items.filter(i => !responses[i.id]?.conforme !== undefined);
    
    if (itemsNonRepondus.length > 0) {
      toast.error(`${itemsNonRepondus.length} items non vérifiés`);
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await api.post('/checklist-materiel/remplir', {
        template_id: selectedTemplate.id,
        materiel_id: materielId,
        reponses: Object.values(responses),
        commentaire_general: commentaireGeneral
      });
      
      setResultat(res.data);
      
      if (res.data.utilisation_autorisee) {
        toast.success('Checklist validée - Matériel OK');
      } else {
        toast.error('ATTENTION: Matériel BLOQUÉ');
      }
      
      loadData();
      
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur soumission');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Réinitialiser le formulaire
  const resetForm = () => {
    setSelectedTemplate(null);
    setMaterielId('');
    setResponses({});
    setCommentaireGeneral('');
    setResultat(null);
  };
  
  // Calculer la progression
  const calculateProgress = () => {
    if (!selectedTemplate) return 0;
    const total = selectedTemplate.items?.length || 0;
    const repondus = Object.values(responses).filter(r => r.conforme !== undefined).length;
    return total > 0 ? Math.round((repondus / total) * 100) : 0;
  };
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ClipboardCheck className="w-8 h-8" />
            Checklist Matériel
          </h1>
          <p className="text-muted-foreground">Vérification du matériel avec alertes automatiques</p>
        </div>
      </div>
      
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-500">{stats.conformes}</p>
              <p className="text-sm text-muted-foreground">Conformes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-yellow-500">{stats.partiels}</p>
              <p className="text-sm text-muted-foreground">Partiels</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-500">{stats.bloques}</p>
              <p className="text-sm text-muted-foreground">Bloqués</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold">{stats.taux_conformite_moyen}%</p>
              <p className="text-sm text-muted-foreground">Taux moyen</p>
            </CardContent>
          </Card>
        </div>
      )}
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="nouvelle">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Checklist
          </TabsTrigger>
          <TabsTrigger value="historique">
            <History className="w-4 h-4 mr-2" />
            Historique
          </TabsTrigger>
          <TabsTrigger value="templates">
            <FileText className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
        </TabsList>
        
        {/* Nouvelle Checklist */}
        <TabsContent value="nouvelle" className="space-y-4">
          {resultat ? (
            /* Résultat de la checklist */
            <Card className={resultat.utilisation_autorisee ? 'border-green-500' : 'border-red-500'}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {resultat.utilisation_autorisee ? (
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                  )}
                  {resultat.utilisation_autorisee ? 'Matériel Validé' : 'Matériel BLOQUÉ'}
                </CardTitle>
                <CardDescription>
                  Taux de conformité: {resultat.taux_conformite}%
                </CardDescription>
              </CardHeader>
              <CardContent>
                {resultat.alertes?.length > 0 && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertTriangle className="w-4 h-4" />
                    <AlertDescription>
                      <p className="font-medium">Alertes détectées:</p>
                      <ul className="list-disc ml-4 mt-2">
                        {resultat.alertes.map((a, i) => (
                          <li key={i}>{a.message}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
                
                {resultat.items_non_conformes?.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-medium">Items non conformes:</p>
                    {resultat.items_non_conformes.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm p-2 bg-red-50 dark:bg-red-950/20 rounded">
                        <XCircle className="w-4 h-4 text-red-500" />
                        <span>{item.item}</span>
                        {item.obligatoire && <Badge variant="destructive">Obligatoire</Badge>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
              <CardFooter>
                <Button onClick={resetForm}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle checklist
                </Button>
              </CardFooter>
            </Card>
          ) : (
            /* Formulaire */
            <>
              {/* Sélection template et matériel */}
              <Card>
                <CardHeader>
                  <CardTitle>Configuration</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Template de checklist</Label>
                      <Select 
                        value={selectedTemplate?.id || ''} 
                        onValueChange={(v) => {
                          const tmpl = templates.find(t => t.id === v);
                          setSelectedTemplate(tmpl);
                          setResponses({});
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner un template" />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.nom} ({t.items?.length || 0} items)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Identifiant du matériel</Label>
                      <Input
                        placeholder="Ex: TRACT-001, MB-2024..."
                        value={materielId}
                        onChange={(e) => setMaterielId(e.target.value)}
                      />
                    </div>
                  </div>
                  
                  {templates.length === 0 && (
                    <Alert>
                      <AlertCircle className="w-4 h-4" />
                      <AlertDescription>
                        Aucun template. Créez-en un dans l'onglet Templates.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
              
              {/* Items de la checklist */}
              {selectedTemplate && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>{selectedTemplate.nom}</CardTitle>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground">
                          {Object.keys(responses).length} / {selectedTemplate.items?.length || 0}
                        </span>
                        <Progress value={calculateProgress()} className="w-32" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[500px] pr-4">
                      <div className="space-y-3">
                        {selectedTemplate.items?.map((item) => (
                          <ChecklistItem
                            key={item.id}
                            item={item}
                            response={responses[item.id]}
                            onResponseChange={handleResponseChange}
                          />
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                  <CardFooter className="flex-col gap-4">
                    <div className="w-full">
                      <Label>Commentaire général (optionnel)</Label>
                      <Textarea
                        placeholder="Observations, remarques..."
                        value={commentaireGeneral}
                        onChange={(e) => setCommentaireGeneral(e.target.value)}
                      />
                    </div>
                    <Button 
                      className="w-full" 
                      size="lg"
                      onClick={handleSubmit}
                      disabled={submitting || calculateProgress() < 100}
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Valider la checklist
                    </Button>
                  </CardFooter>
                </Card>
              )}
            </>
          )}
        </TabsContent>
        
        {/* Historique */}
        <TabsContent value="historique">
          <Card>
            <CardHeader>
              <CardTitle>Historique des vérifications</CardTitle>
            </CardHeader>
            <CardContent>
              {historique.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Aucune checklist enregistrée
                </p>
              ) : (
                <div className="space-y-2">
                  {historique.map((check) => (
                    <div 
                      key={check.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        {check.statut === 'conforme' && <CheckCircle className="w-5 h-5 text-green-500" />}
                        {check.statut === 'partiel' && <AlertTriangle className="w-5 h-5 text-yellow-500" />}
                        {check.statut === 'bloque' && <XCircle className="w-5 h-5 text-red-500" />}
                        <div>
                          <p className="font-medium">{check.materiel_id}</p>
                          <p className="text-sm text-muted-foreground">
                            {check.template_nom} • {format(new Date(check.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={check.statut === 'conforme' ? 'default' : check.statut === 'bloque' ? 'destructive' : 'secondary'}>
                          {check.taux_conformite}%
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Templates */}
        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle>Générer des templates par défaut</CardTitle>
              <CardDescription>
                Créez automatiquement des checklists standards pour vos types de matériel
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { type: 'tracteur', nom: 'Tracteur', icon: Tractor },
                  { type: 'moissonneuse', nom: 'Moissonneuse', icon: Truck },
                  { type: 'pulverisateur', nom: 'Pulvérisateur', icon: Droplet },
                  { type: 'remorque', nom: 'Remorque/Benne', icon: Truck },
                  { type: 'vendangeuse', nom: 'Vendangeuse', icon: Tractor }
                ].map(({ type, nom, icon: Icon }) => (
                  <Button
                    key={type}
                    variant="outline"
                    className="h-24 flex-col gap-2"
                    onClick={() => genererTemplate(type)}
                  >
                    <Icon className="w-8 h-8" />
                    <span>{nom}</span>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* Liste des templates existants */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Templates existants</CardTitle>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  Aucun template créé
                </p>
              ) : (
                <div className="space-y-2">
                  {templates.map((t) => (
                    <div key={t.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{t.nom}</p>
                        <p className="text-sm text-muted-foreground">
                          {t.items?.length || 0} items • {t.frequence}
                        </p>
                      </div>
                      <Badge>{t.type_materiel}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
